import React, { useState, useMemo } from 'react';
import { getImageUrl } from '../utils/questionHtml';

interface QuestionHtmlWithZoomProps {
  html: string;
  imageWidths?: number[];
  onWidthChange?: (newWidths: number[]) => void;
  imageLayout?: string;
}

export default function QuestionHtmlWithZoom({ html, imageWidths = [], onWidthChange, imageLayout = 'auto' }: QuestionHtmlWithZoomProps) {
  const [localWidths, setLocalWidths] = useState<number[]>([]);

  const effectiveWidths = useMemo(() => {
    if (imageWidths && imageWidths.length > 0) return imageWidths;
    if (localWidths.length > 0) return localWidths;
    return [];
  }, [imageWidths, localWidths]);

  const { textParts, imageUrls } = useMemo(() => {
    const imgRegex = /<img[^>]*src=["']([^"']+)["'][^>]*>/gi;
    const images: string[] = [];
    let match;
    while ((match = imgRegex.exec(html)) !== null) {
      images.push(getImageUrl(match[1]));
    }
    const text = html.replace(/<img[^>]*>/gi, '').replace(/<p[^>]*>\s*<\/p>/gi, '').trim();
    return { textParts: text, imageUrls: images };
  }, [html]);

  const gridColsClass = useMemo(() => {
    if (imageLayout === 'cols1') return 'grid-cols-1';
    if (imageLayout === 'cols2') return 'grid-cols-2';
    if (imageLayout === 'cols3') return 'grid-cols-3';
    if (imageLayout === 'cols4') return 'grid-cols-4';
    return 'grid-cols-1';
  }, [imageLayout]);

  const handleWidthChange = (idx: number, val: number) => {
    const newWidth = Math.max(10, Math.min(100, val));
    const newWidths = [...effectiveWidths];
    while (newWidths.length < imageUrls.length) newWidths.push(50);
    newWidths[idx] = newWidth;
    setLocalWidths(newWidths);
    onWidthChange?.(newWidths);
  };

  if (imageUrls.length === 0 || imageLayout === 'auto') {
    return (
      <div
        className="question-html-content prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: html.replace(/src="([^"]+)"/g, (m, src) => `src="${getImageUrl(src)}"`).replace(/src='([^']+)'/g, (m, src) => `src="${getImageUrl(src)}"`) }}
      />
    );
  }

  return (
    <div className="question-html-content">
      {textParts && (
        <div
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: textParts }}
        />
      )}
      <div className={`grid gap-3 ${gridColsClass}`}>
        {imageUrls.map((url, idx) => {
          const w = effectiveWidths[idx] ?? 50;
          return (
            <div key={idx} className="relative">
              <img
                src={url}
                alt={`题目图片 ${idx + 1}`}
                className="rounded-lg border border-gray-200"
                style={{ width: `${w}%`, maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                loading="lazy"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              {onWidthChange && (
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-xs text-gray-500">缩放:</span>
                  <input
                    type="range" min="10" max="100" step="1" value={w}
                    onChange={(e) => handleWidthChange(idx, parseInt(e.target.value))}
                    className="w-20 h-1 accent-blue-500 cursor-pointer"
                  />
                  <input
                    type="number" min="10" max="100" value={w}
                    onChange={(e) => {
                      const v = parseInt(e.target.value);
                      if (!isNaN(v) && v >= 10 && v <= 100) handleWidthChange(idx, v);
                    }}
                    className="w-8 h-4 text-xs text-center border border-gray-300 rounded focus:border-blue-500 outline-none"
                  />
                  <span className="text-xs text-gray-400">%</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}